import { Phone, Mail, MapPin, Clock, Facebook, Instagram, ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { LOGO } from "@/lib/images";
import { track } from "@/lib/analytics";
import { api, formatApiErrorDetail } from "@/lib/api";

const navLinks = [
  { label: "Group Travel", to: "/group-travel" },
  { label: "Cruises", to: "/cruises" },
  { label: "Resorts & Escapes", to: "/resorts" },
  { label: "Honeymoons", to: "/honeymoons" },
  { label: "Destinations", to: "/destinations" },
  { label: "Thailand 2027", to: "/Thailand2027" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Start Your Journey", to: "/start-your-journey" },
  { label: "Ready to Book", to: "/ReadyToBook" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email address."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers", { first_name: "", email, source_page: "footer", campaign_source: "footer_capture" });
      track("join_passport_list", { source_page: "footer" });
      toast.success(data.message || "You're on the list.");
      setJoined(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer data-testid="site-footer" className="bg-noir border-t border-white/10 pt-24 pb-10 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto">
        <Reveal>
          <img src={LOGO} alt="Inforno Travel" className="h-16 w-auto mb-8" />
          <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-6">The Passport Experience</p>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight max-w-4xl">
            Where Will Your Passport Take You Next?
          </h2>
        </Reveal>

        {/* End-of-page Passport List capture — slim, email-only, reuses /api/subscribers */}
        <Reveal delay={0.05}>
          <div className="mt-10 max-w-2xl">
            {joined ? (
              <p data-testid="footer-pl-success" className="flex items-center gap-3 text-gold font-body text-sm">
                <span className="w-8 h-8 border border-gold/50 flex items-center justify-center shrink-0"><Check size={14} /></span>
                You're on the list. Watch your inbox for what's next.
              </p>
            ) : (
              <form onSubmit={join} data-testid="footer-pl-form" className="flex flex-col sm:flex-row sm:items-end gap-5">
                <div className="flex-1">
                  <label htmlFor="footer-pl-email" className="block font-body text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1">
                    Join the Passport List — new experiences, curated trips and inspiration
                  </label>
                  <input id="footer-pl-email" data-testid="footer-pl-email" type="email" inputMode="email" autoComplete="email"
                    placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-white/25 py-2.5 font-body text-sm text-ink placeholder-white/35 focus:border-gold focus:outline-none transition-colors" />
                </div>
                <button type="submit" data-testid="footer-pl-submit" disabled={loading}
                  className="shrink-0 inline-flex items-center justify-center gap-2 border border-gold text-gold px-7 py-3 font-body text-[11px] uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors duration-300 disabled:opacity-50">
                  {loading ? "Joining…" : "Join"} <ArrowRight size={13} />
                </button>
              </form>
            )}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-20 border-t border-white/10 pt-16">
          <div className="md:col-span-4">
            <p className="font-body text-sm leading-relaxed text-white/60 max-w-md font-light">
              Inforno Travel designs unforgettable travel experiences through personalized planning, expert guidance,
              and exceptional service. From luxury cruises and all-inclusive resorts to group travel, destination
              celebrations, and custom itineraries, our Travel Experience Specialists create journeys as memorable as
              the destinations themselves.
            </p>
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-white/40 mt-6">
              Luxury Group Travel Specialists
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a href="https://www.facebook.com/infornotravel" target="_blank" rel="noopener noreferrer"
                 aria-label="Inforno Travel on Facebook" data-testid="footer-facebook"
                 className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/60 transition-colors duration-300">
                <Facebook size={16} strokeWidth={1.5} />
              </a>
              <a href="https://www.instagram.com/infornotravel" target="_blank" rel="noopener noreferrer"
                 aria-label="Inforno Travel on Instagram" data-testid="footer-instagram"
                 className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/60 transition-colors duration-300">
                <Instagram size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-6 space-y-5">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Contact</p>
            <div className="flex items-start gap-3 text-white/70 text-sm">
              <MapPin size={15} className="text-gold mt-0.5" />
              <span>Inforno Travel<br />2 Massachusetts Avenue NE, Suite 75464<br />Washington, DC 20013</span>
            </div>
            <a href="tel:+12028165560" data-testid="footer-phone" onClick={() => track("contact_phone_click", { source_page: window.location.pathname })} className="flex items-center gap-3 text-white/70 hover:text-gold transition-colors text-sm">
              <Phone size={15} className="text-gold" /> (202) 816-5560
            </a>
            <a href="mailto:info@infornotravel.com" data-testid="footer-email" onClick={() => track("contact_email_click", { source_page: window.location.pathname })} className="flex items-center gap-3 text-white/70 hover:text-gold transition-colors text-sm break-all">
              <Mail size={15} className="text-gold" /> info@infornotravel.com
            </a>
            <div className="flex items-start gap-3 text-white/70 text-sm">
              <Clock size={15} className="text-gold mt-0.5" />
              <span>Mon–Fri · 8:30 AM – 5:30 PM ET</span>
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-9">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Explore</p>
            <div className="flex flex-col gap-3">
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`footer-nav-${l.to}`}
                  className="text-left text-white/60 hover:text-gold transition-colors text-sm">{l.label}</Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-11">
            <p className="font-body text-[11px] uppercase tracking-[0.3em] text-gold mb-4">Hours</p>
            <p className="font-body text-white/60 text-sm leading-relaxed">Monday–Friday<br />8:30 AM – 5:30 PM ET</p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-white/40 text-xs">
          <span>© 2026 Inforno Travel. All rights reserved.</span>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-gold transition-colors">Terms</Link>
            <Link to="/accessibility" data-testid="footer-accessibility" className="hover:text-gold transition-colors">Accessibility Statement</Link>
            <span className="tracking-[0.2em] uppercase">infornotravel.com</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
